import axios from 'axios';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import { UserContext } from './authContext';
import { useAlert } from './alertContext';
import { CodeStarNotifications } from 'aws-sdk';
import logger from '../helpers/Logger';
import { DocumentContext } from './documentContext';
import { SearchHistory, useSearchHistory } from './searchHistoryContext';
import { documentTableItem } from '../lib/models/interfaces';

export interface Notification {
  _id: string;
  message: string;
  sub: string;
  email: string;
  customerId: string;
  createdAt: string;
  updatedAt: string;
  read: boolean;
  __v: number;
  documentId: documentTableItem[];
}

interface NotificationContextValue {
  notifications: Notification[];
  deleteNotification: (_id: string) => void;
  showBadge: boolean;
  markNotificationsRead: () => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined
);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { allDocuments, setAllDocuments, getAllDocs } =
    useContext(DocumentContext);
  const { history, setHistory, setSharedHistory, sharedHistory } =
    useSearchHistory();
  const [notificationSet, setNotificationSet] = useState<Set<Notification>>(
    () => new Set()
  );
  const notifications = useMemo(
    () => Array.from(notificationSet),
    [notificationSet]
  );
  const [showBadge, setShowBadge] = useState<boolean>(false);
  const { token, userM } = useContext(UserContext);
  const alert = useAlert();
  const originalFaviconRef = useRef<string | null>(null);

  // ws setup

  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const heartbeatRef = useRef<number | null>(null);
  const shouldReconnectRef = useRef(true);
  const backoffRef = useRef(300);
  const listenersRef = useRef(new Set<(msg: JSON) => void>());
  const heartbeatMs = 5 * 60 * 1000;
  const maxBackoffMs = 1 * 1000;
  const [currentConnectionId, setCurrentConnectionId] = useState<string>('');

  const startHeartbeat = () => {
    if (!heartbeatMs) return;
    stopHeartbeat();
    heartbeatRef.current = window.setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' })); // backend should ignore or reply "pong"
      }
    }, heartbeatMs) as unknown as number;
  };
  const stopHeartbeat = () => {
    if (heartbeatRef.current) {
      window.clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  const addNotification = useCallback((notification: Notification) => {
    setNotificationSet((prev) => {
      const next = new Set(prev);
      for (const existing of next) {
        if (existing._id === notification._id) {
          next.delete(existing);
          break;
        }
      }
      next.add(notification);
      return next;
    });
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotificationSet((prev) => {
      const next = new Set(prev);
      for (const existing of next) {
        if (existing._id === id) {
          next.delete(existing);
          break;
        }
      }
      return next;
    });
  }, []);

  const connect = useCallback(() => {
    if (!token) return; // don’t connect until you have a token

    const customHeaders = {
      Authorization: `Bearer ${token}`,
      type: 'notification'
    };
    const queryParams = new URLSearchParams(customHeaders).toString();
    let pingInterval: NodeJS.Timeout;
    const ws = new WebSocket(
      `wss://e99cr5vf84.execute-api.us-west-2.amazonaws.com/dev?${queryParams}`
    );

    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      backoffRef.current = 300;
      startHeartbeat();
      // optional: identify as notifications channel
      ws.send(JSON.stringify({ type: 'subscribe', channel: 'notifications' }));
      ws.send(
        JSON.stringify({
          action: 'connectionId',
          message: [
            {
              messageToken: `${token}`
            }
          ]
        })
      );
    };

    ws.onmessage = (evt) => {
      let data: any;
      try {
        data = JSON.parse(evt.data);
        if (data.type === 'connectionId') {
          setConnected(data.data.connectionId);
        }
        if (data.type === 'notification') {
          addNotification(data.data);
        }
        if (data.type === 'notification-read') {
          // markAllAsRead();
          // console.log('data read', data);
        }
        if (data.type === 'notification-delete') {
          const id = data?.data?._id;
          console.log('_id in delete', id);
          removeNotification(id);
        }
        if (data.type === 'document-status-update') {
          // set the new document status.
          const updatedDocument = data.data.document;
          const tempAllDocs = allDocuments;
          const findIndex = tempAllDocs.findIndex(
            (ele) => ele._id === updatedDocument._id
          );
          if (findIndex !== -1) {
            tempAllDocs[findIndex].status = updatedDocument.status;
            setAllDocuments(tempAllDocs);
          }
        }
        if (data.type === 'new-search-record') {
          const newHistory = data.data.history as SearchHistory;
          setHistory((prevState) => [newHistory, ...prevState]);

          // console.log('new history array', history);
        }
        if (data.type === 'new-shared-search-record') {
          const newSharedHistory = data.data.history as SearchHistory;
          setSharedHistory((prevState) => [newSharedHistory, ...prevState]);
          logger.info('running get all from notification on docs');
          getAllDocs();
        }
        // console.log('data from message', data);
      } catch {}
      // If you only want server-pushed notifications, filter here
    };

    const teardown = () => {
      setConnected(false);
      stopHeartbeat();
    };

    ws.onclose = () => {
      teardown();
      // Reconnect while token is present (user still on app) and we didn't intentionally stop
      if (token && shouldReconnectRef.current) {
        const delay = Math.min(backoffRef.current, maxBackoffMs);
        setTimeout(connect, delay);
        backoffRef.current = Math.min(backoffRef.current * 2, maxBackoffMs);
      }
    };

    ws.onerror = () => {
      // let onclose handle reconnection/cleanup
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, addNotification, removeNotification]);

  // manage connection lifecycle on token change
  useEffect(() => {
    if (!token || !userM) {
      // wsRef.current?.close();
      return;
    }
    if (userM?.preferences?.notifications) {
      shouldReconnectRef.current = true;
      connect();
    } else {
      shouldReconnectRef.current = false;
    }
    return () => {
      shouldReconnectRef.current = false;
      stopHeartbeat();
      wsRef.current?.close();
    };
  }, [token, connect, userM]);

  // close quickly when user leaves the site completely
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        wsRef.current?.close();
      } catch {}
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined' && !originalFaviconRef.current) {
      originalFaviconRef.current =
        document.querySelector<HTMLLinkElement>("link[rel*='icon']")?.href ??
        '/favicon.ico';
    }

    if (notifications.length > 0) {
      const show = notifications.some((notification) => !notification.read);
      setShowBadge(show);
      if (show) {
        addNotificationDot();
      } else {
        clearNotificationDot();
      }
    } else {
      setShowBadge(false);
      clearNotificationDot();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  const send = useCallback(
    (msg: any) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        console.log('no ws connection');
        return;
      }
      ws.send(JSON.stringify(msg));
    },
    [wsRef]
  );

  const getAllNotifications = async () => {
    if (!token) return;
    try {
      console.log('sending get all');
      send({
        action: 'notifications',
        message: [{ type: 'getAll', messageToken: `${token}` }]
      });
    } catch (e) {
      const error = e as Error;
      console.log('error', error);
      alert.handleError(e);
      // throw new Error(error.message);
    }
  };

  useEffect(() => {
    if (!token || !connected) return;
    getAllNotifications();
    const intervalId = setInterval(() => {
      getAllNotifications();
    }, 10000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, connected]);

  const deleteNotification = async (_id: string) => {
    if (!token) {
      throw new Error('No Auth');
    }
    try {
      send({
        action: 'notifications',
        message: [
          { type: 'delete', messageToken: `${token}`, notificationId: _id }
        ]
      });
    } catch (e) {
      const error = e as Error;
      throw new Error(error.message);
    }
  };
  // const markAllAsRead = () => {
  //   const tempNotifications = notifications;
  //   tempNotifications.forEach((not) => (not.read = true));
  //   setNotifications(tempNotifications);
  // };
  const markNotificationsRead = async () => {
    try {
      send({
        action: 'notifications',
        message: [{ type: 'markRead', messageToken: `${token}` }]
      });
    } catch (e) {
      const error = e as Error;
      throw new Error(error.message);
    }
  };

  // if there are unread notifications add a dot to the title.

  function changeFavicon(src: string) {
    if (typeof document === 'undefined') return;

    const head = document.head || document.getElementsByTagName('head')[0];
    if (!head) return;

    let link =
      document.querySelector<HTMLLinkElement>("link[rel*='icon']") ?? null;

    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      head.appendChild(link);
    }

    link.href = src;
  }
  function addNotificationDot() {
    if (typeof document === 'undefined') return;

    const currentIcon =
      document.querySelector<HTMLLinkElement>("link[rel*='icon']")?.href ??
      originalFaviconRef.current ??
      '/favicon.ico';

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = currentIcon;

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const size = 32; // favicon size
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, size, size);

      // Draw a red circle in the top-right corner
      ctx.beginPath();
      ctx.arc(size - 6, 6, 5, 0, 2 * Math.PI);
      ctx.fillStyle = 'red';
      ctx.fill();

      changeFavicon(canvas.toDataURL('image/png'));
    };

    img.onerror = () => {
      const fallback = originalFaviconRef.current ?? '/favicon.ico';
      changeFavicon(fallback);
    };
  }

  function clearNotificationDot() {
    const fallback = originalFaviconRef.current ?? '/favicon.ico';
    changeFavicon(fallback); // restore to default
  }

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        deleteNotification,
        showBadge,
        markNotificationsRead
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};
